import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { addAppScrollListener, getAppElementScrollTop, getAppScrollTop, getAppViewport, scrollAppTo } from './appViewport';

const entryPositions = new Map();
const destinationPositions = new Map();
const entryPreviewOrigins = new Map();
const destinationPreviewOrigins = new Map();
const restoredConversationOrigins = new Set();
const homePaths = new Set(['/', '/home']);

const visibleHomeAskEntry = () => {
    const inputSelector = '.homeAskEntry, textarea.homeChatInput';
    // Native desktop inputs stay scoped to Home so the standalone Ask textarea
    // cannot become the return-focus target while its route is still mounted.
    const entry = document.querySelector('[data-home-pinned-ask]')?.querySelector(inputSelector);
    return entry && !entry.closest('[hidden]') ? entry : null;
};

const destinationKey = (location) => (
    `${homePaths.has(location.pathname) ? '/' : location.pathname}${location.search || ''}`
);

const isLocalOrigin = (origin) => (
    origin && typeof origin.pathname === 'string' &&
    origin.pathname.startsWith('/') && !origin.pathname.startsWith('//') &&
    !/[\\?#]/.test(origin.pathname) &&
    Array.from(origin.pathname).every((character) => character.charCodeAt(0) >= 32) &&
    origin.pathname !== '/ask'
);

// Sign-in creates a new history entry, so the old Ask back flag is no longer
// valid. Keep only navigation context; replaying an old prefill would overwrite
// the user's edited draft after the session hands it to the signed-in account.
export const getAskSigninReturnState = (state) => {
    const candidate = state?.askOrigin;
    if (!isLocalOrigin(candidate) || ['/sign_in', '/signin', '/signin_new_user'].includes(candidate.pathname)) {
        return { askCanGoBack: false };
    }
    const backgroundLocation = {
        pathname: candidate.pathname,
        search: typeof candidate.search === 'string' && candidate.search.startsWith('?') ? candidate.search : '',
        hash: typeof candidate.hash === 'string' && candidate.hash.startsWith('#') ? candidate.hash : '',
        state: candidate.state ?? null,
        key: typeof candidate.key === 'string' ? candidate.key : 'ask-origin',
    };
    return {
        backgroundLocation,
        askOrigin: {
            ...backgroundLocation,
            scrollY: Number.isFinite(candidate.scrollY) ? Math.max(0, candidate.scrollY) : 0,
            ...(Number.isFinite(candidate.homeFeedTop) ? { homeFeedTop: candidate.homeFeedTop } : {}),
            ...(['entry', 'navigation'].includes(candidate.returnFocus) ? { returnFocus: candidate.returnFocus } : {}),
        },
        askCanGoBack: false,
    };
};

export const getAskBackgroundLocation = (location) => {
    if (location.pathname !== '/ask') return null;
    const background = location.state?.backgroundLocation;
    return isLocalOrigin(background) ? background : null;
};

export const rememberRouteScroll = (location, scrollY = getAppScrollTop()) => {
    if (!location || location.pathname === '/ask') return;
    const position = Math.max(0, Number(scrollY) || 0);
    if (location.key) entryPositions.set(location.key, position);
    destinationPositions.set(destinationKey(location), position);
};

const findForYouPreviewCard = (previewId) => (
    Array.from(document.querySelectorAll('[data-for-you-preview-id]'))
        .find(element => element.getAttribute('data-for-you-preview-id') === previewId)
);

const clearPreviewOrigin = (origin) => {
    if (!origin) return;
    for (const [key, savedOrigin] of entryPreviewOrigins) {
        if (savedOrigin === origin) entryPreviewOrigins.delete(key);
    }
    for (const [key, savedOrigin] of destinationPreviewOrigins) {
        if (savedOrigin === origin) destinationPreviewOrigins.delete(key);
    }
};

// Capture the feed card before playback changes either the current preview or
// the document scroll. Its viewport offset also survives a reordered feed.
export const rememberForYouPreviewOrigin = (location, previewId) => {
    if (!location || location.pathname === '/ask' || !previewId) return;
    const id = String(previewId);
    const card = findForYouPreviewCard(id);
    const top = card ? card.getBoundingClientRect().top - getAppViewport().top : undefined;
    const origin = {
        previewId: id,
        scrollY: Math.max(0, getAppScrollTop() || 0),
        ...(Number.isFinite(top) ? { viewportOffset: top } : {}),
    };
    rememberRouteScroll(location, origin.scrollY);
    if (location.key) entryPreviewOrigins.set(location.key, origin);
    destinationPreviewOrigins.set(destinationKey(location), origin);
};

export const resetRouteScrollPositions = () => {
    entryPositions.clear();
    destinationPositions.clear();
    entryPreviewOrigins.clear();
    destinationPreviewOrigins.clear();
    restoredConversationOrigins.clear();
};

export const useAskNavigation = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const savedOrigin = location.state?.askOrigin;
    const origin = isLocalOrigin(savedOrigin) ? savedOrigin : null;
    const originLabel = origin?.pathname.startsWith('/library') || origin?.pathname === '/import_shows'
        ? 'Library'
        : origin?.pathname.startsWith('/menu') || origin?.pathname.startsWith('/settings')
            ? 'More'
            : 'Home';

    const openAsk = useCallback((options = {}) => {
        const hasReplacement = Object.prototype.hasOwnProperty.call(options, 'text');
        if (location.pathname === '/ask') {
            if (hasReplacement) {
                navigate(location, {
                    replace: true,
                    state: { ...location.state, askReplacementText: String(options.text ?? '') },
                });
            }
            return;
        }

        // A prefill link should return to browsing Home without reopening itself.
        const search = new URLSearchParams(location.search);
        const removePrefill = homePaths.has(location.pathname) && search.has('q');
        if (removePrefill) search.delete('q');
        const backgroundLocation = {
            ...location,
            search: removePrefill
                ? (search.toString() ? `?${search.toString()}` : '')
                : location.search,
        };
        const scrollY = Math.max(0, getAppScrollTop() || 0);
        const returnFocus = options.returnFocus === 'navigation' || !homePaths.has(location.pathname)
            ? 'navigation' : 'entry';
        const entryElement = returnFocus === 'navigation'
            ? document.querySelector('.bottomMenuButton[aria-current="page"]')
            : options.triggerElement || visibleHomeAskEntry();
        const feed = entryElement?.closest('[data-home-pinned-ask]')
            ? document.getElementById('explore') : null;
        const homeFeedTop = feed ? getAppElementScrollTop(feed) : undefined;
        rememberRouteScroll(backgroundLocation, scrollY);
        navigate('/ask', {
            replace: Boolean(options.replace),
            state: {
                backgroundLocation,
                askOrigin: {
                    ...backgroundLocation, scrollY, returnFocus,
                    ...(Number.isFinite(homeFeedTop) ? { homeFeedTop } : {}),
                },
                askCanGoBack: !options.replace,
                ...(hasReplacement ? { askReplacementText: String(options.text ?? '') } : {}),
            },
        });
    }, [location, navigate]);

    const closeAsk = useCallback(() => {
        if (origin && location.state?.askCanGoBack) {
            navigate(-1);
            return;
        }
        if (origin) {
            rememberRouteScroll(origin, origin.scrollY);
            navigate({ pathname: origin.pathname, search: origin.search || '', hash: origin.hash || '' }, {
                replace: true,
                state: origin.state,
            });
            return;
        }
        navigate('/', { replace: true });
    }, [location.state, navigate, origin]);

    return { openAsk, closeAsk, originLabel };
};

export const useHomeAskPrefill = () => {
    const location = useLocation();
    const { openAsk } = useAskNavigation();
    const params = new URLSearchParams(location.search);
    const hasPrefill = homePaths.has(location.pathname) && params.has('q');
    const prefill = params.get('q');
    useEffect(() => {
        if (hasPrefill) openAsk({ text: prefill, replace: true });
    }, [hasPrefill, openAsk, prefill]);
    return hasPrefill;
};

// Restore page scrolling independently of the focused Ask viewport. Save on
// scroll, not unmount: hiding a background route can clamp its offset to zero.
export const RouteScrollRestoration = ({ suspended = false }) => {
    const location = useLocation();
    const navigationType = useNavigationType();
    const previousLocation = useRef(null);

    useLayoutEffect(() => {
        const previousRestoration = window.history.scrollRestoration;
        window.history.scrollRestoration = 'manual';
        return () => { window.history.scrollRestoration = previousRestoration; };
    }, []);

    useLayoutEffect(() => {
        const previous = previousLocation.current;
        previousLocation.current = location;
        if (suspended || location.pathname === '/ask') return undefined;

        // Conversations replacing Ask carry its origin so returning Home also
        // compensates for draft/suggestion height changes above the feed.
        const previousOrigin = isLocalOrigin(previous?.state?.askOrigin) ? previous.state.askOrigin : null;
        const returningFromAsk = previousOrigin &&
            (previous.pathname === '/ask' || !restoredConversationOrigins.has(previous.key)) &&
            previousOrigin.pathname === location.pathname &&
            (previousOrigin.search || '') === location.search &&
            (previousOrigin.hash || '') === location.hash;
        if (returningFromAsk && previous.pathname !== '/ask') {
            // Later Back/Forward visits restore the user's newer Home scroll.
            restoredConversationOrigins.add(previous.key);
        }
        // Signup is a fresh Home entry. Its saved speaker-picker offset should
        // not follow it, but later Back/Forward and Ask returns still restore.
        const scrollToTop = location.state?.scrollToTop === true &&
            navigationType !== 'POP' && !returningFromAsk;
        if (scrollToTop) {
            clearPreviewOrigin(entryPreviewOrigins.get(location.key));
            clearPreviewOrigin(destinationPreviewOrigins.get(destinationKey(location)));
            rememberRouteScroll(location, 0);
            scrollAppTo(0, 0);
        }
        const savedEntry = entryPositions.get(location.key);
        const isRootDestination = homePaths.has(location.pathname) ||
            location.pathname === '/library' || location.pathname === '/menu';
        const savedDestination = destinationPositions.get(destinationKey(location));
        const previewOrigin = !scrollToTop && !returningFromAsk
            ? navigationType === 'POP'
                ? entryPreviewOrigins.get(location.key)
                : isRootDestination
                    ? destinationPreviewOrigins.get(destinationKey(location))
                    : undefined
            : undefined;
        const savedPosition = scrollToTop ? 0 : previewOrigin
            ? previewOrigin.scrollY : returningFromAsk
            ? previous.state.askOrigin.scrollY
            : navigationType === 'POP' && savedEntry !== undefined
                ? savedEntry
                : isRootDestination && savedDestination !== undefined
                    ? savedDestination
                    : 0;
        const targetExplore = location.hash === '#explore' &&
            homePaths.has(location.pathname) && !returningFromAsk && !scrollToTop &&
            !previewOrigin &&
            !(navigationType === 'POP' && savedEntry !== undefined);

        let restoring = true;
        let observer;
        let timer;
        let frame;
        let focusRestored = false;
        let settledTarget;
        const finish = () => {
            if (!restoring) return;
            restoring = false;
            observer?.disconnect();
            window.clearTimeout(timer);
            window.cancelAnimationFrame(frame);
            if (previewOrigin) {
                // Once browsing resumes, subsequent navigation should restore
                // that new position rather than repeatedly pinning this card.
                clearPreviewOrigin(previewOrigin);
            }
            rememberRouteScroll(location);
        };
        const restore = () => {
            if (!restoring) return;
            let target = savedPosition;
            const originCard = previewOrigin ? findForYouPreviewCard(previewOrigin.previewId) : null;
            if (originCard && Number.isFinite(previewOrigin.viewportOffset)) {
                target = Math.max(0, getAppElementScrollTop(originCard) - previewOrigin.viewportOffset);
            }
            if (returningFromAsk && Number.isFinite(previousOrigin.homeFeedTop)) {
                const feed = document.getElementById('explore');
                if (feed) {
                    // Drafts can change the fixed header's clearance and the
                    // suggestions above the feed. Preserve the visible feed.
                    target = Math.max(0, savedPosition + getAppElementScrollTop(feed) - previousOrigin.homeFeedTop);
                }
            }
            if (targetExplore) {
                const section = document.getElementById('explore');
                if (!section) return;
                const dock = document.querySelector('[data-home-ask-dock]');
                const dockRect = dock?.getBoundingClientRect();
                const visibleTop = Math.max(getAppViewport().top, dockRect?.top || 0);
                const topClearance = visibleTop + (dockRect?.height || 0);
                target = Math.max(0, getAppScrollTop() + section.getBoundingClientRect().top - topClearance - 12);
            }
            scrollAppTo(0, target);
            if (((returningFromAsk && homePaths.has(location.pathname)) || targetExplore) &&
                (!Number.isFinite(settledTarget) || Math.abs(settledTarget - target) > 0.5)) {
                settledTarget = target;
                // Let Home measure its fixed header after unhiding the retained
                // route or jumping to an anchor, before restoring focus.
                frame = window.requestAnimationFrame(restore);
                return;
            }
            if (returningFromAsk && !focusRestored) {
                const navigationFocus = previousOrigin.returnFocus === 'navigation' || !homePaths.has(location.pathname);
                const entry = navigationFocus
                    ? document.querySelector('.bottomMenuButton[aria-current="page"]')
                    : visibleHomeAskEntry();
                if (entry && !entry.disabled && !entry.closest('[hidden]')) {
                    entry.focus({ preventScroll: true });
                    focusRestored = true;
                }
            }
            // Home can initially mount a loading placeholder. Keep observing
            // for the original card; the saved numeric offset is the fallback
            // if a refreshed feed no longer contains it.
            const awaitingOriginCard = previewOrigin && Number.isFinite(previewOrigin.viewportOffset) && !originCard;
            if (Math.abs(getAppScrollTop() - target) < 2 && (!returningFromAsk || focusRestored) && !awaitingOriginCard) finish();
        };
        const onScroll = () => { if (!restoring) rememberRouteScroll(location); };
        const onUserInteraction = () => finish();
        const removeScrollListener = addAppScrollListener(onScroll);
        window.addEventListener('wheel', onUserInteraction, { passive: true });
        window.addEventListener('touchstart', onUserInteraction, { passive: true });
        window.addEventListener('keydown', onUserInteraction);
        observer = new MutationObserver(() => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(restore);
        });
        observer.observe(document.body, { childList: true, subtree: true, attributes: true });
        timer = window.setTimeout(finish, 5000);
        frame = window.requestAnimationFrame(restore);

        return () => {
            restoring = false;
            observer?.disconnect();
            window.clearTimeout(timer);
            window.cancelAnimationFrame(frame);
            removeScrollListener();
            window.removeEventListener('wheel', onUserInteraction);
            window.removeEventListener('touchstart', onUserInteraction);
            window.removeEventListener('keydown', onUserInteraction);
        };
    }, [location, navigationType, suspended]);

    return null;
};
